import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { useDashboard, useCompleteTask, useUpload } from "@/api/hooks";
import { useTheme } from "@/hooks/useTheme";
import OnboardingOverlay from "@/components/onboarding/OnboardingOverlay";
import AppHeader from "@/components/layout/AppHeader";
import GiveawayFooter from "@/components/giveaway/GiveawayFooter";
import ProfileStatsBar from "@/components/campaign-profile/ProfileStatsBar";
import ProfileTasksList from "@/components/campaign-profile/ProfileTasksList";
import TaskModal from "@/components/dashboard/TaskModal";
import ManualReviewSubmissionModal from "@/components/dashboard/ManualReviewSubmissionModal";
import ReferralTaskModal from "@/components/dashboard/ReferralTaskModal";
import { isManualReviewTask, getTaskStatus } from "@/lib/taskUtils";
const MAX_POINTS = 602;

export default function CampaignProfileTasks() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoadingAuth: authLoading } = useAuth();
  const { data, isLoading } = useDashboard();
  const { themeId, onboardingDone, applyTheme, markDone } = useTheme(data?.user);
  const completeTask = useCompleteTask();
  const upload = useUpload();

  const [selectedTask, setSelectedTask] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [optimisticStatuses, setOptimisticStatuses] = useState({});
  // Auth is handled by ProtectedRoute wrapper in App.jsx

  const handleTaskSelect = (task) => {
    if (getTaskStatus(task, optimisticStatuses) === "pending_review") return;
    setSelectedTask(task);
  };

  const handleComplete = async (task, submissionData) => {
    setCompleting(true);
    try {
      const res = await completeTask.mutateAsync({
        taskId: task.id,
        submission_data: submissionData,
      });
      setSelectedTask(null);
      const pts = res.points_earned;
      setSuccessMsg(`+${pts} נקודות נוספו!`);
      setTimeout(() => setSuccessMsg(null), 3500);
      const count = pts >= 200 ? 200 : pts >= 50 ? 120 : 60;
      confetti({ particleCount: count, spread: 80, origin: { y: 0.5 }, colors: ["#c8f135", "#ffffff", "#ff3dac", "#3dffea"] });
    } catch (err) {
      console.error("Failed to complete task:", err);
    }
    setCompleting(false);
  };

  const handleReviewSubmit = async (task, payload) => {
    setCompleting(true);
    setOptimisticStatuses((prev) => ({ ...prev, [task.id]: "pending_review" }));
    setSelectedTask(null);
    setSuccessMsg("המשימה נשלחה לבדיקה");
    setTimeout(() => setSuccessMsg(null), 3500);
    try {
      const uploadRes = await upload.mutateAsync(payload.file);
      await completeTask.mutateAsync({
        taskId: task.id,
        proof_url: uploadRes.url,
        metadata: { reference_text: payload.reference_text },
      });
    } catch (err) {
      console.error("Failed to submit for review:", err);
    }
    setCompleting(false);
  };

  if (authLoading || !isAuthenticated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#000" }}>
        <div className="w-8 h-8 border-4 border-transparent rounded-full animate-spin"
          style={{ borderTopColor: "#c8f135" }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#000", color: "#c4c4c4" }}>
        <p>לא נמצאו נתונים</p>
      </div>
    );
  }

  const { tasks, user: campaignUser } = data;
  const openTaskCount = tasks.filter((t) => !t.is_completed && !t.is_maxed).length;

  return (
    <div className="min-h-screen overflow-x-hidden w-full" style={{ background: "#000" }}>
      <AppHeader />

      {onboardingDone === false && (
        <OnboardingOverlay
          themeId={themeId}
          onThemeChange={applyTheme}
          onComplete={markDone}
        />
      )}

      <div data-onboard="points-header">
      <ProfileStatsBar
        user={{
          ...campaignUser,
          total_points: campaignUser.campaign_points ?? campaignUser.total_points,
        }}
        totalParticipants={campaignUser.total_participants}
        openTaskCount={openTaskCount}
        maxPoints={data.contest?.max_points ?? MAX_POINTS}
      />
      </div>

      {/* Success toast */}
      <AnimatePresence>
        {successMsg && (
          <motion.div
            key="toast"
            initial={{ opacity: 0, scale: 0.85, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85 }}
            className="text-center py-3 px-6 rounded-full font-bold text-sm mx-auto max-w-xs"
            style={{ background: "rgba(200,241,53,0.15)", color: "#c8f135", border: "1px solid rgba(200,241,53,0.3)" }}
          >
            {successMsg}
          </motion.div>
        )}
      </AnimatePresence>

      <div data-onboard="task-list">
      <ProfileTasksList
        tasks={tasks}
        onTaskSelect={handleTaskSelect}
        optimisticStatuses={optimisticStatuses}
      />
      </div>

      <GiveawayFooter />

      {/* Modals */}
      <AnimatePresence>
        {selectedTask && getTaskStatus(selectedTask, optimisticStatuses) !== "pending_review" && (
          selectedTask.task_type === "referral" ? (
            <ReferralTaskModal
              key={selectedTask.id}
              task={selectedTask}
              referralToken={campaignUser.referral_code}
              onClose={() => setSelectedTask(null)}
            />
          ) : isManualReviewTask(selectedTask) ? (
            <ManualReviewSubmissionModal
              key={selectedTask.id}
              task={selectedTask}
              submitting={completing}
              onClose={() => setSelectedTask(null)}
              onSubmit={handleReviewSubmit}
            />
          ) : (
            <TaskModal
              key={selectedTask.id}
              task={selectedTask}
              completing={completing}
              onClose={() => setSelectedTask(null)}
              onComplete={handleComplete}
            />
          )
        )}
      </AnimatePresence>
    </div>
  );
}
